import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { TransitionGroup } from "react-transition-group";

// Material
import { Box, Collapse, List, Divider } from "@mui/material";
// Hooks
import { useNotificationList } from "@/Common/hooks/Notifications/useNotificationList";
import { useIntersection } from "@/Common/hooks/Layout/useIntersection";
// Components
import { NotificationListItem, NotificationListFooter, NotificationsSkeleton } from "@/Layouts/views/Header/components/Notifications";

interface INotificationPropsList {
	enabled?: boolean;
	showOnlyUnread: boolean;
}

export const NotificationList = ({ showOnlyUnread, enabled }: Readonly<INotificationPropsList>) => {
	const [selected, setSelected] = useState("");

	const { notifications, isLoading, isFetching, fetchNextPage, hasNextPage } = useNotificationList({
		enabled,
		showOnlyUnread,
	});

	const handleFetchNextPage = useCallback(() => {
		if (isLoading || isFetching || !hasNextPage) {
			return;
		}
		fetchNextPage({ cancelRefetch: false });
	}, [isLoading, isFetching, fetchNextPage, hasNextPage]);

	const { wrapperContainerRef, observedElementRef } = useIntersection({
		rootMargin: "0px 0px 200px 0px",
		onScrolledIntoView: handleFetchNextPage,
	});

	const MemoizedList = useMemo(
		() => (
			<Box ref={wrapperContainerRef} className={clsx("overflow-y-auto max-h-[380px] my-[5px] mx-1")}>
				<List className="!py-0">
					<TransitionGroup>
						{notifications.map((item) => (
							<Collapse key={item._id}>
								<NotificationListItem item={item} selected={selected} setSelected={setSelected} />
								<Box className="px-2">
									<Divider variant="middle" className="w-full !mx-0" />
								</Box>
							</Collapse>
						))}
						<Collapse>{hasNextPage && <NotificationsSkeleton count={1} />}</Collapse>
						<Collapse>{hasNextPage && <NotificationsSkeleton count={1} />}</Collapse>
					</TransitionGroup>
				</List>
				<span ref={observedElementRef} className="w-0 h-0"></span>
				<NotificationListFooter hasItems={notifications.length > 0 || hasNextPage} />
			</Box>
		),
		[hasNextPage, notifications, selected, observedElementRef, wrapperContainerRef]
	);

	if (isLoading) {
		return (
			<>
				<NotificationsSkeleton />
				<NotificationListFooter hasItems={true} />
			</>
		);
	}

	if (!notifications.length) {
		return <NotificationListFooter hasItems={false} />;
	}

	return MemoizedList;
};
